<template>
    <form :action="formAction" method="post" ref="paymentForm" @submit.prevent="processCardAndSubmit" id="payment-form">
        <slot></slot>
        <fieldset>
            <legend>Billing Details</legend>
            <div class="mod" v-if="formLocked">
                <div class="text-block bd">
                    <p style="font-size: small">If your billing address is incorrect, please update it in your <a href="/account/profile">profile</a></p>
                </div>
            </div>
            <div class="row">
                <div class="col" style-size="1of2">
                    <div class="mod">
                        <ul class="form-fields list-spaced">
                            <li class="field-show-label">
                                <label for="cardholder-name">Name</label>
                                <input id="cardholder-name" placeholder="Name" type="text" :value="fullName" :disabled="formLocked">
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="col" style-size="1of2">
                    <div class="mod">
                        <ul class="form-fields list-spaced">
                            <li class="field-show-label">
                                <label for="cardholder-email">Email</label>
                                <input id="cardholder-email" placeholder="Email" type="email" :value="user.email" :disabled="formLocked">
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col" style-size="1of2">
                    <div class="mod">
                        <ul class="form-fields list-spaced">
                            <li class="field-show-label">
                                <label for="cardholder-phone">Phone</label>
                                <input id="cardholder-phone" placeholder="Phone" type="tel" :value="user.telephone" :disabled="formLocked">
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="mod">
                    <ul class="form-fields list-spaced">
                        <li class="field-show-label">
                            <label for="cardholder-address">Address</label>
                            <input id="cardholder-address" placeholder="Address" type="text" :value="user.address" :disabled="formLocked">
                        </li>
                    </ul>
                </div>
            </div>
            <div class="row">
                <div class="col" style-size="1of3">
                    <div class="mod">
                        <ul class="form-fields list-spaced">
                            <li class="field-show-label">
                                <label for="cardholder-city">City</label>
                                <input id="cardholder-city" placeholder="City" type="text" :value="user.city" :disabled="formLocked">
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="col" style-size="1of3">
                    <div class="mod">
                        <ul class="form-fields list-spaced">
                            <li class="field-show-label">
                                <label for="cardholder-state">State</label>
                                <input id="cardholder-state" placeholder="State" type="text" :value="user.state" :disabled="formLocked">
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="col" style-size="1of3">
                    <div class="mod">
                        <ul class="form-fields list-spaced">
                            <li class="field-show-label">
                                <label for="cardholder-zip">ZIP</label>
                                <input id="cardholder-zip" placeholder="ZIP" type="text" :value="user.zip" :disabled="formLocked">
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="mod">
                    <ul class="form-fields">
                        <li class="field-show-label" ref="cardBox">
                            <label for="card-element">Credit or debit card</label>
                            <div id="card-element"></div>
                            <div class="validation-message" ref="cardErrors"></div>
                        </li>
                    </ul>
                </div>
            </div>
            <input type="hidden" id="stripe_token" ref="paymentMethodId" name="stripe_token" v-model="paymentMethodId">
        </fieldset>
        <div class="row">
            <div class="mod">
                <input ref="submitButton" id="payment-button" type="submit" class="button" :value="buyButtonName">
            </div>
        </div>
    </form>
</template>

<script>
    export default {
        name: "CardForm",
        props: {
            user: {
                type: Object,
                default: function () {
                    return {
                        name_first: null,
                        name_last: null,
                        address: null,
                        city: null,
                        state: null,
                        zip: null,
                        email: null,
                        telephone: null,
                    }
                }
            },
            formAction: {
                type: String,
                required: true
            },
            formLocked: {
                type: Boolean,
                default: false
            },
            submitName: {
                type: String,
                required: true
            },
            clientSecret: {
                type: String,
                required: true
            },
            isPaymentIntent: {
                type: Boolean,
                default: false
            }
        },
        data: function () {
            return {
                fullName: this.user.name_first + ' ' + this.user.name_last,
                paymentMethodId: null,
                stripe: null,
                token: null,
                cardElement: null,
                buyButtonName: this.submitName
            }
        },
        mounted() {
            this.token = document.head.querySelector('meta[name="s-pub"]').content;

            this.setUpCard();
        },

        methods: {
            setUpCard: async function () {
                if (!window.Stripe) await this.waitForStripe();
                this.stripe = window.Stripe(this.token);

                const style = {
                    base: {
                        fontSize: '18px',
                        color: "#555",
                        fontFamily: '"Karbon Regular", "Helvetica Neue", Helvetica, sans-serif',
                        fontSmoothing: 'antialiased',
                        '::placeholder': {
                            color: '#aab7c4'
                        },
                    }
                };

                const elements = this.stripe.elements();
                this.cardElement = elements.create('card', {style});

                this.cardElement.mount('#card-element');

                this.cardElement.addEventListener('change', ({error}) => {
                    if (error) {
                        this.$refs.cardBox.classList.add('validation', 'validation-error');
                        this.$refs.cardErrors.textContent = error.message;
                    } else {
                        this.$refs.cardBox.classList.remove('validation', 'validation-error');
                        this.$refs.cardErrors.textContent = '';
                    }
                });
            },
            waitForStripe: async function () {
                while (!window.Stripe) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
                return true;
            },
            processCardAndSubmit: async function (e) {
                if (!this.isPaymentIntent) {
                    this.$refs.submitButton.disabled = true;
                    this.buyButtonName = "Working...";
                    await this.setupIntent(e);
                } else {
                    await this.paymentIntent(e);
                }
            },
            paymentIntent: async function (e) {
                const { paymentIntent, error } = await this.stripe.confirmCardPayment(
                    this.clientSecret, {
                        payment_method: {
                            card: this.cardElement,
                            billing_details: {
                                name: this.fullName,
                                email: this.user.email,
                                phone: this.user.telephone,
                                address: {
                                    city: this.user.city,
                                    line1: this.user.address,
                                    state: this.user.state
                                }
                            }
                        }
                    }
                );
                if (error) {
                    this.$refs.cardBox.classList.add('validation', 'validation-error');
                    this.$refs.cardErrors.textContent = error.message;
                    this.$refs.submitButton.disabled = false;
                    this.$refs.submitButton.value = this.buyButtonName;
                } else {
                    window.vm1.showModal = true;
                    // Payment success
                }
            },
            setupIntent: async function (e) {
                const { setupIntent, error } = await this.stripe.confirmCardSetup(
                    this.clientSecret, {
                        payment_method: {
                            card: this.cardElement,
                            billing_details: {
                                name: this.fullName,
                                email: this.user.email,
                                phone: this.user.telephone,
                                address: {
                                    city: this.user.city,
                                    line1: this.user.address,
                                    state: this.user.state
                                }
                            }
                        }
                    }
                );
                if (error) {
                    this.$refs.cardBox.classList.add('validation', 'validation-error');
                    this.$refs.cardErrors.textContent = error.message;
                    this.$refs.submitButton.disabled = false;
                    this.buyButtonName = this.submitName;
                } else {
                    this.paymentMethodId = setupIntent.payment_method;
                    this.$refs.paymentMethodId.value = setupIntent.payment_method;
                    this.$refs.paymentForm.submit();
                }
            }
        }
    }
</script>

<style scoped>
    .StripeElement {
        border: 2px solid #aaa;
        border-radius: 5px;
        padding: 6px 14px;
        line-height: 1.4;
    }
</style>
